// @ts-nocheck
import * as React from "react";
import { useLoaderData } from "react-router-dom";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Line, Scatter, Radar, PolarArea, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  ArcElement,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

import Header from "../components/Header";
import BehaviorAssessmentHistoryTable from "../components/reports/BehaviorAssessmentHistoryTable";
import { listAnimals } from "../models/animals";
import { getAssessment, listAssessmentsByAnimal } from "../models/assessments";
import { listSectionAsByAssessment } from "../models/sectiona";
import { listSectionBsByAssessment } from "../models/sectionb";
import { getFacility } from "../models/facility";
import { getUser } from "../models/users";
import { getFinalEvaluation } from "../models/finalevaluation";
import { date, dayjs } from "../utils/datetime";
import { sectionBBehaviors } from "../utils/sectionBBehaviors";
import {
  round1,
  averageAdjectiveComposite,
  sectionBBehaviorFrequencyPercents,
} from "../utils/sections";

ChartJS.register(CategoryScale, LinearScale, RadialLinearScale, ArcElement, BarElement, PointElement, LineElement, Tooltip, Legend);

const parseBoolean = (inputValue, defaultValue) => {
  if (inputValue === undefined || inputValue === null || inputValue === "") {
    return defaultValue;
  }
  if (inputValue === true || inputValue === "true" || inputValue === "1") {
    return true;
  }
  if (inputValue === false || inputValue === "false" || inputValue === "0") {
    return false;
  }
  return defaultValue;
};

const parseNumber = (inputValue, defaultValue) => {
  const value = parseInt(inputValue, 10);
  return Number.isNaN(value) ? defaultValue : value;
};

const toScore = (inputValue) => {
  const value = typeof inputValue === "number" ? inputValue : Number(inputValue);
  return Number.isFinite(value) ? value : 0;
};


const frequencyText = (score) => {
  switch (score) {
    case 12:
    case 11:
      return "Almost every time";
    case 10:
    case 9:
    case 8:
      return "Most but not every time";
    case 7:
    case 6:
      return "Half or more of the times";
    case 5:
      return "Less than half";
    case 4:
    case 3:
      return "Around once a day";
    case 2:
    case 1:
      return "Less than daily";
    case 0:
    default:
      return "Never";
  }
};

const QBA_COLOR_POSITIVE_CALM = "rgb(31, 119, 180)";
const QBA_COLOR_NEGATIVE_CALM = "rgb(127, 127, 127)";
const QBA_COLOR_POSITIVE_ACTIVE = "rgb(188, 189, 34)";
const QBA_COLOR_NEGATIVE_ACTIVE = "rgb(23, 190, 207)";
const QBA_COLORS = {
  positive_calm: QBA_COLOR_POSITIVE_CALM,
  negative_calm: QBA_COLOR_NEGATIVE_CALM,
  positive_active: QBA_COLOR_POSITIVE_ACTIVE,
  negative_active: QBA_COLOR_NEGATIVE_ACTIVE,
};
const toAlpha = (rgbValue, alpha) => rgbValue.replace("rgb", "rgba").replace(")", `, ${alpha})`);
const colorFromBehaviorKey = (behaviorKey, index) => {
  // Deterministic fallback color when behavior config does not provide one.
  const seed = String(behaviorKey || index)
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const r = 40 + (seed * 53) % 176;
  const g = 40 + (seed * 97) % 176;
  const b = 40 + (seed * 193) % 176;
  return `rgb(${r}, ${g}, ${b})`;
};

const behaviorFrequencyText = (frequencyPercent) => {
  if (!frequencyPercent) return frequencyText(0);
  const scaledCount = Math.round((frequencyPercent / 100) * 12);
  return frequencyText(scaledCount);
};

const formatPercent = (value) => `${round1(value)}%`;
const nextPercentAxisStep = (value) => {
  if (value <= 25) return 5;
  if (value <= 50) return 5;
  return 10;
};

const normalizeTextValue = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object") {
          if (typeof parsed.label === "string" && parsed.label.trim()) return parsed.label.trim();
          if (typeof parsed.description === "string" && parsed.description.trim()) return parsed.description.trim();
        }
      } catch (error) {
        // Keep original string if it is not valid JSON.
      }
    }
    return trimmed;
  }
  if (typeof value === "number") return String(value);
  if (value && typeof value === "object") {
    if (typeof value.label === "string" && value.label.trim()) return value.label.trim();
    if (typeof value.description === "string" && value.description.trim()) return value.description.trim();
  }
  return "";
};

const aggregateTextCounts = (values = []) => {
  const counts = values.reduce((acc, value) => {
    const key = normalizeTextValue(value);
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .sort((a, b) => {
      if (b[1] === a[1]) return a[0].localeCompare(b[0]);
      return b[1] - a[1];
    })
    .map(([label, count]) => `${label}\u00A0(${count})`);
};

const ReportSubheader = ({ children }) => (
  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
    {children}
  </Typography>
);

const ChartSeriesControls = ({ onActivateAll, onDeactivateAll }) => (
  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
    <Button size="small" variant="outlined" onClick={onActivateAll}>
      Activate all series
    </Button>
    <Button size="small" variant="outlined" onClick={onDeactivateAll}>
      Deactivate all series
    </Button>
  </Stack>
);

const InfoBlock = ({ items }) => (
  <Grid container spacing={2}>
    {items.map((item) => (
      <Grid key={item.label} xs={12} sm={6}>
        <Typography variant="body2" color="text.secondary">
          {item.label}
        </Typography>
        <Typography variant="body1">{item.value}</Typography>
      </Grid>
    ))}
  </Grid>
);

export async function loader({ params }) {
  const { assessmentId, previousCount, enforceCurrentComplete, enforcePastComplete } = params;
  const previousToInclude = parseNumber(previousCount, 4);
  const enforceCurrent = parseBoolean(enforceCurrentComplete, true);
  const enforcePast = parseBoolean(enforcePastComplete, true);

  const assessment = await getAssessment(assessmentId);
  if (!assessment) {
    throw new Error("Assessment not found.");
  }
  if (enforceCurrent && assessment.submitted_on === null) {
    throw new Error("Assessment is incomplete and enforcement is enabled.");
  }

  const animals = await listAnimals();
  const animal = animals.find((cur) => cur.id === assessment.animal_id);
  const facility = await getFacility();
  const user = await getUser(assessment.user_id);

  const allAssessments = await listAssessmentsByAnimal(assessment.animal_id);
  const pastAssessments = allAssessments.filter((cur) => {
    if (cur.id === assessment.id) return false;
    if (cur.is_visibile_in_reports === false) return false;
    if (!cur.started_on || !assessment.started_on) return false;
    return cur.started_on.isBefore(assessment.started_on) || cur.started_on.isSame(assessment.started_on);
  }).filter((cur) => {
    if (!enforcePast) return true;
    return cur.submitted_on !== null;
  }).sort((a, b) => {
    if (a.started_on.isBefore(b.started_on)) return 1;
    if (a.started_on.isAfter(b.started_on)) return -1;
    return 0;
  }).slice(0, previousToInclude);

  const reportAssessments = [assessment, ...pastAssessments];
  const reportRecords = await Promise.all(reportAssessments.map(async (cur) => {
    const sectionAs = await listSectionAsByAssessment(cur.id);
    const sectionBs = await listSectionBsByAssessment(cur.id);
    const finalEvaluation = await getFinalEvaluation(cur.id);
    return { assessment: cur, sectionAs, sectionBs, finalEvaluation };
  }));

  return {
    assessment,
    animal,
    facility,
    user,
    animals,
    reportRecords,
    previousToInclude,
    enforceCurrent,
    enforcePast,
  };
}

export default function AssessmentReportPage({ title = "Assessment Report" }) {
  const { assessment, animal, facility, user, animals, reportRecords, previousToInclude } = useLoaderData();
  const overallSeriesLabels = ["Mental health", "Physical health", "Overall welfare"];
  const [overallSeriesVisibility, setOverallSeriesVisibility] = React.useState(() => (
    Object.fromEntries(overallSeriesLabels.map((label) => [label, true]))
  ));

  const keeperInfo = [
    { label: "Keeper Name", value: user?.name || "Unknown" },
    { label: "Keeper Email", value: user?.email || "Unknown" },
  ];

  const previousAssessmentsCount = Math.max(reportRecords.length - 1, 0);
  const reportInfo = [
    { label: "Animal Collection", value: facility?.name || "Unknown" },
    { label: "Animal Name", value: animal?.name || "Unknown" },
    { label: "Genus", value: animal?.genus || "Unknown" },
    { label: "Sex", value: animal?.sex || "Unknown" },
    { label: "Completed Observations", value: reportRecords.length },
    { label: "Previous Assessments Requested", value: previousToInclude },
    { label: "Previous Assessments Fetched", value: previousAssessmentsCount },
    { label: "Date of most recent observation", value: date(assessment.started_on) },
  ];

  const observationDates = reportRecords
    .map((cur) => cur.assessment.started_on)
    .filter(Boolean);
  const sortedDates = observationDates
    .map((cur) => dayjs(cur))
    .sort((a, b) => a.valueOf() - b.valueOf());
  const earliest = sortedDates.length ? sortedDates[0] : dayjs(assessment.started_on);
  const latest = sortedDates.length ? sortedDates[sortedDates.length - 1] : dayjs(assessment.started_on);

  reportInfo.push({
    label: "Time frame for all observations submitted",
    value: `${date(earliest)} to ${date(latest)}`,
  });

  const currentRecord = reportRecords[0];
  const overallAssessmentRows = [
    { label: "Mental health", score: round1(toScore(currentRecord?.finalEvaluation?.mental_health)) },
    { label: "Physical health", score: round1(toScore(currentRecord?.finalEvaluation?.physical_health)) },
    { label: "Overall welfare", score: round1(toScore(currentRecord?.finalEvaluation?.overall_welfare)) },
  ];
  const compositeDefinitions = [
    {
      key: "positive_calm",
      section: "A: QBA",
      label: "Positive Calm",
      context: "Content, calm, comfortable, at ease, relaxed",
      getValue: (record) => averageAdjectiveComposite(record.sectionAs, ["content", "calm", "comfortable", "at_ease", "relaxed"]),
    },
    {
      key: "negative_calm",
      section: "A: QBA",
      label: "Negative Calm",
      context: "Withdrawn, tense, listless, wary, indifferent",
      getValue: (record) => averageAdjectiveComposite(record.sectionAs, ["withdrawn", "tense", "listless", "wary", "indifferent"]),
    },
    {
      key: "positive_active",
      section: "A: QBA",
      label: "Positive Active",
      context: "Lively, playful, sociable, curious, excited",
      getValue: (record) => averageAdjectiveComposite(record.sectionAs, ["lively", "playful", "sociable", "curious", "excited"]),
    },
    {
      key: "negative_active",
      section: "A: QBA",
      label: "Negative Active",
      context: "Frustrated, irritable, agitated, anxious, fearful",
      getValue: (record) => averageAdjectiveComposite(record.sectionAs, ["frustrated", "irritable", "agitated", "anxious", "fearful"]),
    },
  ];
  const qbaDefinitions = compositeDefinitions;

  const compositeScores = currentRecord
    ? compositeDefinitions.map((definition) => ({
        section: definition.section,
        label: definition.label,
        context: definition.context,
        value: definition.getValue(currentRecord),
      }))
    : [];

  const trendChartRecords = [...reportRecords].sort((a, b) => {
    const aDate = dayjs(a.assessment.started_on);
    const bDate = dayjs(b.assessment.started_on);
    return aDate.valueOf() - bDate.valueOf();
  });
  const overallAssessmentTrendData = {
    labels: trendChartRecords.map((record) => date(record.assessment.started_on)),
    datasets: [
      {
        label: "Mental health",
        data: trendChartRecords.map((record) => round1(toScore(record?.finalEvaluation?.mental_health))),
        borderColor: "rgb(31, 119, 180)",
        backgroundColor: "rgb(31, 119, 180)",
        hidden: overallSeriesVisibility["Mental health"] === false,
        tension: 0.2,
        fill: false,
        pointRadius: 3,
      },
      {
        label: "Physical health",
        data: trendChartRecords.map((record) => round1(toScore(record?.finalEvaluation?.physical_health))),
        borderColor: "rgb(44, 160, 44)",
        backgroundColor: "rgb(44, 160, 44)",
        hidden: overallSeriesVisibility["Physical health"] === false,
        tension: 0.2,
        fill: false,
        pointRadius: 3,
      },
      {
        label: "Overall welfare",
        data: trendChartRecords.map((record) => round1(toScore(record?.finalEvaluation?.overall_welfare))),
        borderColor: "rgb(214, 39, 40)",
        backgroundColor: "rgb(214, 39, 40)",
        hidden: overallSeriesVisibility["Overall welfare"] === false,
        tension: 0.2,
        fill: false,
        pointRadius: 3,
      },
    ],
  };
  const overallAssessmentTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      y: {
        min: 0,
        max: 10,
        ticks: { stepSize: 1 },
        title: {
          display: true,
          text: "Score (0-10)",
        },
      },
    },
  };

  const behaviorStackChartRecords = [...reportRecords].sort((a, b) => {
    const aDate = dayjs(a.assessment.started_on);
    const bDate = dayjs(b.assessment.started_on);
    return aDate.valueOf() - bDate.valueOf();
  });
  const behaviorDefinitions = sectionBBehaviors.map((item) => ({
    ...item,
    key: item.behaviorKey,
    label: item.title,
    color: item.color,
  }));
  const [sectionBSeriesVisibility, setSectionBSeriesVisibility] = React.useState({});
  const [sectionBBarSeriesVisibility, setSectionBBarSeriesVisibility] = React.useState({});

  React.useEffect(() => {
    setSectionBSeriesVisibility((previous) => (
      Object.fromEntries(behaviorDefinitions.map((definition) => [definition.label, previous[definition.label] ?? true]))
    ));
    setSectionBBarSeriesVisibility((previous) => (
      Object.fromEntries(behaviorDefinitions.map((definition) => [definition.label, previous[definition.label] ?? true]))
    ));
  }, [behaviorDefinitions]);

  const sectionBDatasetsByAssessmentId = reportRecords.reduce((acc, record) => {
    const baseline = behaviorDefinitions.reduce((inner, definition) => {
      inner[definition.key] = {
        key: definition.key,
        label: definition.label,
        count: 0,
        observationCount: 0,
        frequencyPercent: 0,
        frequencyLabel: frequencyText(0),
        details: [],
      };
      return inner;
    }, {});

    const sectionBs = record.sectionBs || [];
    const behaviorPercents = sectionBBehaviorFrequencyPercents(sectionBs);
    sectionBs.forEach((sectionBRecord) => {
      const behaviors = sectionBRecord.behaviors || [];
      behaviorDefinitions.forEach((definition) => {
        const matches = behaviors.filter((behavior) => behavior.behavior === definition.key);
        if (matches.length > 0) {
          baseline[definition.key].observationCount += 1;
          baseline[definition.key].count += matches.length;
          baseline[definition.key].details = baseline[definition.key].details.concat(
            matches.map((behavior) => ({
              observationRange: sectionBRecord.observation_range || "",
              description: behavior.description || "",
              location: behavior.location || "",
              tags: Array.isArray(behavior.tags) ? behavior.tags : [],
              associatedAnimals: (behavior.associated_animals || [])
                .map((id) => animals.find((cur) => cur.id === id)?.name || "")
                .filter(Boolean),
            }))
          );
        }
      });
    });

    Object.values(baseline).forEach((behaviorData) => {
      const percentData = behaviorPercents.behaviors[behaviorData.key];
      behaviorData.count = percentData?.count || 0;
      behaviorData.frequencyPercent = percentData?.percent || 0;
      behaviorData.frequencyLabel = behaviorFrequencyText(behaviorData.frequencyPercent);
    });

    acc[record.assessment.id] = {
      totalObservations: sectionBs.length,
      totalBehaviorRecords: behaviorPercents.totalCount,
      behaviors: baseline,
    };
    return acc;
  }, {});

  const sectionBBehaviorColors = behaviorDefinitions.reduce((acc, definition, index) => {
    acc[definition.key] = definition.color || colorFromBehaviorKey(definition.key, index);
    return acc;
  }, {});

  const behaviorCountsByAssessment = behaviorStackChartRecords.map((record) => {
    const dataset = sectionBDatasetsByAssessmentId[record.assessment.id];
    return behaviorDefinitions.reduce((acc, definition) => {
      acc[definition.key] = dataset?.behaviors?.[definition.key]?.frequencyPercent || 0;
      return acc;
    }, {});
  });
  const behaviorStackChartData = {
    labels: behaviorStackChartRecords.map((record) => date(record.assessment.started_on)),
    datasets: behaviorDefinitions.map((definition) => ({
      label: definition.label,
      data: behaviorCountsByAssessment.map((counts) => counts[definition.key]),
      borderColor: sectionBBehaviorColors[definition.key],
      backgroundColor: toAlpha(sectionBBehaviorColors[definition.key], 0.65),
      hidden: sectionBBarSeriesVisibility[definition.label] === false,
      borderWidth: 1,
      stack: "section_b_behaviors",
    })),
  };
  const behaviorStackChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Section B Behavior Frequency (%)",
        },
      },
    },
  };
  const sectionBFrequencyTrendChartData = {
    labels: behaviorStackChartRecords.map((record) => date(record.assessment.started_on)),
    datasets: behaviorDefinitions.map((definition) => ({
      label: definition.label,
      data: behaviorStackChartRecords.map((record) => {
        const dataset = sectionBDatasetsByAssessmentId[record.assessment.id];
        return dataset?.behaviors?.[definition.key]?.frequencyPercent || 0;
      }),
      borderColor: sectionBBehaviorColors[definition.key],
      backgroundColor: sectionBBehaviorColors[definition.key],
      hidden: sectionBSeriesVisibility[definition.label] === false,
      tension: 0.2,
      fill: false,
      pointRadius: 3,
      pointHoverRadius: 5,
    })),
  };
  const maxSectionBFrequencyPercent = Math.max(
    0,
    ...behaviorDefinitions.flatMap((definition) => (
      behaviorStackChartRecords.map((record) => {
        const dataset = sectionBDatasetsByAssessmentId[record.assessment.id];
        return dataset?.behaviors?.[definition.key]?.frequencyPercent || 0;
      })
    ))
  );
  const sectionBFrequencyAxisMax = Math.min(
    100,
    Math.max(10, (Math.ceil(maxSectionBFrequencyPercent / 5) * 5) + 5)
  );
  const sectionBFrequencyAxisStep = nextPercentAxisStep(sectionBFrequencyAxisMax);
  const sectionBFrequencyTrendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      y: {
        min: 0,
        max: sectionBFrequencyAxisMax,
        ticks: { stepSize: sectionBFrequencyAxisStep },
        title: {
          display: true,
          text: "Behavior frequency (%)",
        },
      },
    },
  };
  const qbaQuadrantPoint = (definition) => {
    const value = currentRecord ? definition.getValue(currentRecord) : 0;
    switch (definition.key) {
      case "positive_calm":
        return { x: -value, y: value };
      case "negative_calm":
        return { x: -value, y: -value };
      case "positive_active":
        return { x: value, y: value };
      case "negative_active":
        return { x: value, y: -value };
      default:
        return { x: 0, y: 0 };
    }
  };
  const qbaQuadrantChartData = {
    datasets: qbaDefinitions.map((definition) => ({
      label: definition.label,
      data: [qbaQuadrantPoint(definition)],
      borderColor: QBA_COLORS[definition.key],
      backgroundColor: QBA_COLORS[definition.key],
      pointRadius: 6,
      pointHoverRadius: 7,
    })),
  };
  const qbaQuadrantPlugin = {
    id: "qbaQuadrants",
    beforeDraw: (chart) => {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea || !scales?.x || !scales?.y) return;

      const { left, right, top, bottom } = chartArea;
      const zeroX = scales.x.getPixelForValue(0);
      const zeroY = scales.y.getPixelForValue(0);

      ctx.save();
      ctx.fillStyle = toAlpha(QBA_COLOR_POSITIVE_CALM, 0.08);
      ctx.fillRect(left, top, zeroX - left, zeroY - top);
      ctx.fillStyle = toAlpha(QBA_COLOR_NEGATIVE_CALM, 0.08);
      ctx.fillRect(left, zeroY, zeroX - left, bottom - zeroY);
      ctx.fillStyle = toAlpha(QBA_COLOR_POSITIVE_ACTIVE, 0.08);
      ctx.fillRect(zeroX, top, right - zeroX, zeroY - top);
      ctx.fillStyle = toAlpha(QBA_COLOR_NEGATIVE_ACTIVE, 0.08);
      ctx.fillRect(zeroX, zeroY, right - zeroX, bottom - zeroY);

      ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
      ctx.font = "12px sans-serif";
      ctx.textBaseline = "top";

      ctx.textAlign = "left";
      ctx.fillText("Positive Calm", left + 8, top + 8);
      ctx.fillText("Positive Active", zeroX + 8, top + 8);
      ctx.textBaseline = "bottom";
      ctx.fillText("Negative Calm", left + 8, bottom - 8);
      ctx.fillText("Negative Active", zeroX + 8, bottom - 8);
      ctx.restore();
    },
    afterDatasetsDraw: (chart) => {
      const { ctx } = chart;
      ctx.save();
      ctx.fillStyle = "rgba(0, 0, 0, 0.9)";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";

      qbaDefinitions.forEach((definition, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        const point = meta?.data?.[0];
        if (!point) return;
        const value = currentRecord ? definition.getValue(currentRecord) : 0;
        ctx.fillText(String(value), point.x + 8, point.y);
      });
      ctx.restore();
    },
  };
  const qbaQuadrantChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: (context) => {
            const definition = qbaDefinitions.find((item) => item.label === context.dataset.label);
            const value = currentRecord && definition ? definition.getValue(currentRecord) : 0;
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear",
        min: -10,
        max: 10,
        grid: {
          color: (context) => (context.tick.value === 0 ? "rgba(0, 0, 0, 0.85)" : "rgba(0, 0, 0, 0.2)"),
          lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
        },
        ticks: { stepSize: 2 },
        title: { display: true, text: "Calm axis" },
      },
      y: {
        min: -10,
        max: 10,
        grid: {
          color: (context) => (context.tick.value === 0 ? "rgba(0, 0, 0, 0.85)" : "rgba(0, 0, 0, 0.2)"),
          lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
        },
        ticks: { stepSize: 2 },
        title: { display: true, text: "Active axis" },
      },
    },
  };
  const qbaRadarOrder = ["positive_calm", "positive_active", "negative_active", "negative_calm"];
  const qbaRadarDefinitions = qbaRadarOrder
    .map((key) => qbaDefinitions.find((definition) => definition.key === key))
    .filter(Boolean);
  const qbaPolarOrder = ["positive_active", "negative_active", "negative_calm", "positive_calm"];
  const qbaPolarDefinitions = qbaPolarOrder
    .map((key) => qbaDefinitions.find((definition) => definition.key === key))
    .filter(Boolean);
  const radarSeriesColor = (index, total) => {
    const hue = Math.round((index * 360) / Math.max(total, 1));
    return {
      border: `hsl(${hue} 70% 42%)`,
      fill: `hsl(${hue} 70% 42% / 0.12)`,
    };
  };
  const qbaRadarData = {
    labels: qbaRadarDefinitions.map((definition) => definition.label),
    datasets: trendChartRecords.map((record, index) => {
      const colors = radarSeriesColor(index, trendChartRecords.length);
      return {
        label: date(record.assessment.started_on),
        data: qbaRadarDefinitions.map((definition) => definition.getValue(record)),
        borderColor: colors.border,
        backgroundColor: colors.fill,
        pointBackgroundColor: colors.border,
        pointRadius: 3,
      };
    }),
  };
  const qbaRadarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
    },
    scales: {
      r: {
        startAngle: 315,
        min: 0,
        max: 10,
        ticks: { stepSize: 2 },
      },
    },
  };
  const qbaPolarAreaData = {
    labels: qbaPolarDefinitions.map((definition) => definition.label),
    datasets: [
      {
        label: "Current Assessment QBA",
        data: qbaPolarDefinitions.map((definition) => (currentRecord ? definition.getValue(currentRecord) : 0)),
        backgroundColor: qbaPolarDefinitions.map((definition) => toAlpha(QBA_COLORS[definition.key], 0.5)),
        borderColor: qbaPolarDefinitions.map((definition) => QBA_COLORS[definition.key]),
        borderWidth: 1,
      },
    ],
  };
  const qbaPolarAreaOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
    },
    scales: {
      r: {
        min: 0,
        max: 10,
        ticks: { stepSize: 2 },
      },
    },
  };

  const behaviorHistorySections = behaviorDefinitions.map((definition) => {
    const rows = reportRecords.map((record) => {
      const dataset = sectionBDatasetsByAssessmentId[record.assessment.id];
      const behaviorData = dataset?.behaviors?.[definition.key];
      const details = behaviorData?.details || [];

      const tags = aggregateTextCounts(details.flatMap((detail) => detail.tags || []));
      const descriptions = [...new Set(details.map((detail) => detail.description).filter(Boolean))];
      const locations = [...new Set(details.map((detail) => detail.location).filter(Boolean))];
      const associatedAnimals = [...new Set(details.flatMap((detail) => detail.associatedAnimals || []).filter(Boolean))];

      return {
        key: `${record.assessment.id}-${definition.key}`,
        assessmentDate: date(record.assessment.started_on),
        behavior: definition.label,
        frequencyValue: behaviorData?.frequencyLabel || frequencyText(0),
        frequencyPercent: formatPercent(behaviorData?.frequencyPercent || 0),
        tags,
        descriptions,
        locations,
        associatedAnimals,
      };
    });

    return {
      ...definition,
      key: definition.key,
      title: `${definition.label} History`,
      rows,
    };
  });

  const currentSectionBBehaviorRows = behaviorDefinitions.map((definition) => {
    const dataset = sectionBDatasetsByAssessmentId[currentRecord?.assessment?.id];
    const behaviorData = dataset?.behaviors?.[definition.key];
    const tags = aggregateTextCounts((behaviorData?.details || []).flatMap((detail) => detail.tags || []));
    return {
      key: definition.key,
      behavior: definition.label,
      totalObservations: dataset?.totalObservations || 0,
      totalBehaviorRecords: dataset?.totalBehaviorRecords || 0,
      observationCount: behaviorData?.observationCount || 0,
      count: behaviorData?.count || 0,
      frequencyLabel: behaviorData?.frequencyLabel || frequencyText(0),
      frequencyPercent: behaviorData?.frequencyPercent || 0,
      tags,
    };
  });
  const currentSectionBBehaviorTotals = currentSectionBBehaviorRows.reduce((acc, row) => ({
    count: acc.count + row.count,
    frequencyPercent: round1(acc.frequencyPercent + row.frequencyPercent),
  }), {
    count: 0,
    frequencyPercent: 0,
  });

  return (
    <Container component="main" maxWidth="md" sx={{ pb: 4 }}>
      <Header label={title} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <Divider />
        <InfoBlock items={keeperInfo} />
        <Divider />
        <InfoBlock items={reportInfo} />
        <Divider />
        <ReportSubheader>About</ReportSubheader>
        <Typography variant="body2">
          This report was generated from completed assessment observations. It summarizes
          Section A (Qualitative Behavior Assessment) and Section B (Ethogram) data for the
          most recent observation and selected past observations.
        </Typography>
        <ReportSubheader>Overall Assessment</ReportSubheader>
        <Typography variant="body2">
          Final evaluation scores for the current assessment and trend across included assessments.
        </Typography>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Current score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {overallAssessmentRows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.score}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box
          sx={{
            mt: 3,
            height: 320,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            position: "relative",
            overflow: "hidden",
            "& canvas": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          }}
        >
          <Line data={overallAssessmentTrendData} options={overallAssessmentTrendOptions} style={{ width: "100%", minWidth: 0 }} />
        </Box>
        <ChartSeriesControls
          onActivateAll={() => setOverallSeriesVisibility(Object.fromEntries(overallSeriesLabels.map((label) => [label, true])))}
          onDeactivateAll={() => setOverallSeriesVisibility(Object.fromEntries(overallSeriesLabels.map((label) => [label, false])))}
        />
        <ReportSubheader>Overview  Average (mean) QBA Score For Each Quadrant Of Emotional Expressivity Scores</ReportSubheader>
        <Typography variant="body2">
          Average scores summarize QBA observations for the most recent completed assessment.
          Scores range from 0 to 10.
        </Typography>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Section</TableCell>
              <TableCell>Average</TableCell>
              <TableCell>Items</TableCell>
              <TableCell align="right">Current Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {compositeScores.map((score) => (
              <TableRow key={score.label}>
                <TableCell>{score.section}</TableCell>
                <TableCell>{score.label}</TableCell>
                <TableCell>{score.context}</TableCell>
                <TableCell align="right">{score.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <ReportSubheader>Section A QBA Quadrant View</ReportSubheader>
        <Typography variant="body2">
          Quadrants represent the four QBA averages from the most recent completed assessment.
        </Typography>
        <Box
          sx={{
            mt: 2,
            height: 360,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            position: "relative",
            overflow: "hidden",
            "& canvas": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          }}
        >
          <Scatter data={qbaQuadrantChartData} options={qbaQuadrantChartOptions} plugins={[qbaQuadrantPlugin]} style={{ width: "100%", minWidth: 0 }} />
        </Box>
        <ReportSubheader>Section A QBA Polar Area</ReportSubheader>
        <Typography variant="body2">
          Polar area view of the four QBA averages for the most recent completed assessment.
        </Typography>
        <Box
          sx={{
            mt: 2,
            height: 360,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            position: "relative",
            overflow: "hidden",
            "& canvas": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          }}
        >
          <PolarArea data={qbaPolarAreaData} options={qbaPolarAreaOptions} style={{ width: "100%", minWidth: 0 }} />
        </Box>
        <ReportSubheader>Section A QBA Radar</ReportSubheader>
        <Typography variant="body2">
          Radar view of the four QBA averages from the most recent completed assessment.
        </Typography>
        <Box
          sx={{
            mt: 2,
            height: 360,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            position: "relative",
            overflow: "hidden",
            "& canvas": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          }}
        >
          <Radar data={qbaRadarData} options={qbaRadarOptions} style={{ width: "100%", minWidth: 0 }} />
        </Box>
        <ReportSubheader>Section B Behavior Frequency Trends</ReportSubheader>
        <Typography variant="body2">
          Line chart of behavior frequency percentages across assessments, based on each
          behavior&apos;s share of all recorded Section B behaviors in that assessment.
        </Typography>
        <Box
          sx={{
            mt: 3,
            height: 360,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            position: "relative",
            overflow: "hidden",
            "& canvas": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          }}
        >
          <Line data={sectionBFrequencyTrendChartData} options={sectionBFrequencyTrendChartOptions} style={{ width: "100%", minWidth: 0 }} />
        </Box>
        <ChartSeriesControls
          onActivateAll={() => setSectionBSeriesVisibility(Object.fromEntries(behaviorDefinitions.map((definition) => [definition.label, true])))}
          onDeactivateAll={() => setSectionBSeriesVisibility(Object.fromEntries(behaviorDefinitions.map((definition) => [definition.label, false])))}
        />
        <ReportSubheader>Section B Relative Frequency of Behaviours by Assessment Date</ReportSubheader>
        <Typography variant="body2">
          Stacked percentage share of individual Section B behaviors per assessment date
          (oldest to latest).
        </Typography>
        <Box
          sx={{
            mt: 2,
            height: 420,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            position: "relative",
            overflow: "hidden",
            "& canvas": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          }}
        >
          <Bar data={behaviorStackChartData} options={behaviorStackChartOptions} style={{ width: "100%", minWidth: 0 }} />
        </Box>
        <ChartSeriesControls
          onActivateAll={() => setSectionBBarSeriesVisibility(Object.fromEntries(behaviorDefinitions.map((definition) => [definition.label, true])))}
          onDeactivateAll={() => setSectionBBarSeriesVisibility(Object.fromEntries(behaviorDefinitions.map((definition) => [definition.label, false])))}
        />
        <ReportSubheader>Section B Behavior Frequency (Current Assessment)</ReportSubheader>
        <Typography variant="body2">
          Overall frequency summary for each configured Section B behavior on the current
          assessment, based on its share of all Section B behavior records.
        </Typography>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Behavior Category</TableCell>
              <TableCell align="right">Frequency %</TableCell>
              <TableCell align="right">Observation windows with behavior</TableCell>
              <TableCell align="right">Behavior records</TableCell>
              <TableCell align="right">All behavior records</TableCell>
              <TableCell>Specific Behaviors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentSectionBBehaviorRows.map((row) => (
              <TableRow key={`current-${row.key}`}>
                <TableCell>{row.behavior}</TableCell>
                <TableCell align="right">{formatPercent(row.frequencyPercent)}</TableCell>
                <TableCell align="right">{row.observationCount} / {row.totalObservations}</TableCell>
                <TableCell align="right">{row.count}</TableCell>
                <TableCell align="right">{row.totalBehaviorRecords}</TableCell>
                <TableCell>
                  {row.tags.length > 0 ? (
                    <Box component="ul" sx={{ m: 0, p: 0, listStylePosition: "inside" }}>
                      {row.tags.map((tag) => (
                        <li key={`${row.key}-${tag}`}>{tag}</li>
                      ))}
                    </Box>
                  ) : "None"}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Totals</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatPercent(currentSectionBBehaviorTotals.frequencyPercent)}</TableCell>
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 600 }}>{currentSectionBBehaviorTotals.count}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {behaviorHistorySections.map((section) => (
        <BehaviorAssessmentHistoryTable key={section.key} title={section.title} rows={section.rows} section={section} />
      ))}

    </Container>
  );
}
